 /**
  * CR307: Deep Merge Utilities for Template Editor Grand Slam
  * 
  * Provides proper deep merge semantics to prevent shallow replacement:
  * - Object + Object → recursive merge
  * - Override is scalar/array/null → override replaces
  * - Override missing → fallback to base
  * 
  * Also provides sanitization to prevent empty override subtrees.
  */
 
 // ================================================================
 // DEEP MERGE
 // ================================================================
 
 /**
  * Deep merge two objects with proper precedence.
  * 
  * @param base - Base object (lower precedence)
  * @param override - Override object (higher precedence)
  * @returns Merged object
  */
 export function deepMerge<T extends Record<string, unknown>>(
   base: T | null | undefined,
   override: T | null | undefined
 ): T {
   // If no base, return override or empty object
   if (!base) {
     return (override ?? {}) as T;
   }
   
   // If no override, return base
   if (!override) {
     return base;
   }
   
   const result: Record<string, unknown> = { ...base };
   
   for (const [key, overrideValue] of Object.entries(override)) {
     const baseValue = result[key];
     
     // If both are objects (non-null, non-array), merge recursively
     if (
       overrideValue !== null &&
       typeof overrideValue === "object" &&
       !Array.isArray(overrideValue) &&
       baseValue !== null &&
       typeof baseValue === "object" &&
       !Array.isArray(baseValue)
     ) {
       result[key] = deepMerge(
         baseValue as Record<string, unknown>,
         overrideValue as Record<string, unknown>
       );
     } else if (overrideValue !== undefined) {
       // Override takes precedence (including null, arrays, primitives)
       result[key] = overrideValue;
     }
   }
   
   return result as T;
 }
 
 /**
  * Deep merge three layers: legacy → template → overrides
  */
 export function deepMerge3<T extends Record<string, unknown>>(
   legacy: T | null | undefined,
   template: T | null | undefined,
   overrides: T | null | undefined
 ): T {
   return deepMerge(deepMerge(legacy, template), overrides);
 }
 
 // ================================================================
 // SANITIZER (D3: Never Again)
 // ================================================================
 
 /**
  * Check if a value is an empty object (no keys).
  */
 function isEmptyObject(value: unknown): boolean {
   return (
     value !== null &&
     typeof value === "object" &&
     !Array.isArray(value) &&
     Object.keys(value as object).length === 0
   );
 }
 
 /**
  * Recursively sanitize an object by removing empty objects.
  * This prevents empty override subtrees from stomping populated templates.
  * 
  * Rules:
  * - Remove empty objects {}
  * - If parent becomes empty after pruning children, remove parent
  * - Preserve null, false, 0, empty string, empty arrays (only prune {})
  * 
  * @param obj - Object to sanitize
  * @returns Sanitized object (or undefined if fully empty)
  */
 export function sanitizeOverrides<T extends Record<string, unknown>>(
   obj: T | null | undefined
 ): T | undefined {
   if (obj === null || obj === undefined) {
     return undefined;
   }
   
   if (typeof obj !== "object" || Array.isArray(obj)) {
     return obj;
   }
   
   const result: Record<string, unknown> = {};
   let hasKeys = false;
   
   for (const [key, value] of Object.entries(obj)) {
     // Skip undefined values
     if (value === undefined) {
       continue;
     }
     
     // If value is an object (non-null, non-array), recursively sanitize
     if (
       value !== null &&
       typeof value === "object" &&
       !Array.isArray(value)
     ) {
       const sanitizedChild = sanitizeOverrides(value as Record<string, unknown>);
       // Only include if child has content
       if (sanitizedChild !== undefined && !isEmptyObject(sanitizedChild)) {
         result[key] = sanitizedChild;
         hasKeys = true;
       }
     } else {
       // Keep all other values (null, primitives, arrays)
       result[key] = value;
       hasKeys = true;
     }
   }
   
   return hasKeys ? (result as T) : undefined;
 }
 
 /**
  * Compare two values and strip override keys that match baseline.
  * This keeps overrides minimal and prevents redundant stomping.
  * 
  * @param override - Override object
  * @param baseline - Baseline object (template or legacy)
  * @returns Pruned override with only differing keys
  */
 export function stripRedundantOverrides<T extends Record<string, unknown>>(
   override: T | null | undefined,
   baseline: T | null | undefined
 ): T | undefined {
   if (!override) {
     return undefined;
   }
   
   if (!baseline) {
     return sanitizeOverrides(override);
   }
   
   const result: Record<string, unknown> = {};
   let hasKeys = false;
   
   for (const [key, overrideValue] of Object.entries(override)) {
     const baselineValue = baseline[key];
     
     // If both are objects, recurse
     if (
       overrideValue !== null &&
       typeof overrideValue === "object" &&
       !Array.isArray(overrideValue) &&
       baselineValue !== null &&
       typeof baselineValue === "object" &&
       !Array.isArray(baselineValue)
     ) {
       const prunedChild = stripRedundantOverrides(
         overrideValue as Record<string, unknown>,
         baselineValue as Record<string, unknown>
       );
       if (prunedChild !== undefined && !isEmptyObject(prunedChild)) {
         result[key] = prunedChild;
         hasKeys = true;
       }
     } else {
       // Compare values - keep only if different
       if (JSON.stringify(overrideValue) !== JSON.stringify(baselineValue)) {
         result[key] = overrideValue;
         hasKeys = true;
       }
     }
   }
   
   return hasKeys ? (result as T) : undefined;
 }
 
 // ================================================================
 // SOURCE TRACKING (D5: Admin Source Inspector)
 // ================================================================
 
 export type ValueSource = "overrides" | "template" | "legacy" | "default";
 
 export interface ValueWithSource<T> {
   value: T;
   source: ValueSource;
   path: string;
 }
 
 /**
  * Trace the source of a value through the merge chain.
  * 
  * @param path - JSON path (e.g., "components.ribbon.styling.baseColorHex")
  * @param overrides - Overrides object
  * @param template - Template object
  * @param legacy - Legacy object
  * @param defaultValue - Default value
  * @returns Value with source information
  */
 export function traceValueSource<T>(
   path: string,
   overrides: Record<string, unknown> | null | undefined,
   template: Record<string, unknown> | null | undefined,
   legacy: Record<string, unknown> | null | undefined,
   defaultValue: T
 ): ValueWithSource<T> {
   const getNestedValue = (obj: Record<string, unknown> | null | undefined, p: string): unknown => {
     if (!obj) return undefined;
     const parts = p.split(".");
     let current: unknown = obj;
     for (const part of parts) {
       if (current === null || current === undefined || typeof current !== "object") {
         return undefined;
       }
       current = (current as Record<string, unknown>)[part];
     }
     return current;
   };
   
   // Check overrides first (highest precedence)
   const overrideValue = getNestedValue(overrides, path);
   if (overrideValue !== undefined) {
     return { value: overrideValue as T, source: "overrides", path };
   }
   
   // Check template
   const templateValue = getNestedValue(template, path);
   if (templateValue !== undefined) {
     return { value: templateValue as T, source: "template", path };
   }
   
   // Check legacy
   const legacyValue = getNestedValue(legacy, path);
   if (legacyValue !== undefined) {
     return { value: legacyValue as T, source: "legacy", path };
   }
   
   // Default
   return { value: defaultValue, source: "default", path };
 }